import {Injectable} from '@angular/core';
import {finalize, Observable, of, shareReplay, tap} from 'rxjs';
import {WorkerDbModel} from 'src/app/models/worker-db.model';
import {LOCAL_STORAGE_CONSTANTS} from '../../constants/local-storage.constants';
import {CryptoService} from '../utils/crypto.service';
import {HttpClientService} from '../utils/http-client.service';
import {LocalstorageService} from '../utils/localstorage.service';

/** What the cache holds: the workers AND the store scope they were fetched for. */
interface CachedWorkers {
  storeId: number | null;
  workers: WorkerDbModel[];
}

@Injectable({
  providedIn: 'root'
})
export class WorkerService {

  /**
   * Requests sent but not yet answered, keyed by store scope.
   *
   * Changing a store fires the advisor fetch twice: once from the whenStoreContextChanges()
   * subscription, and once from the question's own ngOnInit after preSelectStore()'s
   * worker-mismatch cleanup re-renders it. Neither used to cost a request - the list was cached
   * unscoped and only re-filtered locally - so the double trigger was invisible until the fetch
   * became scoped. Sharing the in-flight request collapses them back into one, and does the same
   * for any other caller that races.
   */
  private readonly inFlight = new Map<string, Observable<WorkerDbModel[]>>();

  constructor(
    private httpClientService: HttpClientService,
    private localstorageService: LocalstorageService,
    private cryptoService: CryptoService
  ) { }

  /**
   * @param storeId when a preceding booking question has already answered a store, the backend
   *   returns only that store's workers instead of the whole partner. Omitted/null keeps today's
   *   unscoped call byte-for-byte.
   */
  getWorkers(storeId?: number): Observable<WorkerDbModel[]> {
    const scope = storeId ?? null;

    const cached = this.getWorkersFromLocalStorage(scope);
    if (cached) {
      return of(cached);
    }

    const key = String(scope);
    const pending = this.inFlight.get(key);
    if (pending) {
      return pending;
    }

    // Scoped -> POST workers/filter: partner-scoped, no 200-row cap, and a far lighter payload
    // (it does not contain availabilities / roles / booking_type). Measured on one BKB store:
    // 137 KB / 1.0 s against 716 KB / 2.7 s for the unscoped index.
    // Unscoped stays on GET workers - filter() rejects an empty body with 400, and the widget
    // has no store to scope by at that point anyway.
    const request: Observable<WorkerDbModel[]> = (
      scope === null
        ? this.httpClientService.get('workers', {})
        : this.httpClientService.post('workers/filter', {store_id: scope}, {})
    ).pipe(
      tap((workers: WorkerDbModel[]) => this.setWorkersInLocalStorage(workers, scope)),
      // Cleared on error as well as success, so a failed fetch is retried by the next caller
      // instead of replaying its failure forever.
      finalize(() => this.inFlight.delete(key)),
      shareReplay({bufferSize: 1, refCount: false})
    );

    this.inFlight.set(key, request);
    return request;
  }

  /**
   * A HIT only when the cached entry was fetched for the SAME store scope — a store-scoped list
   * must never be served for a different store, or for the unscoped call. Kept in the single
   * WORKERS_KEY rather than one key per store: clearWidgetKeys() removes that key by its exact
   * name, so per-store keys would survive a `refresh=1` and go stale.
   */
  getWorkersFromLocalStorage(storeId: number | null = null): WorkerDbModel[] {
    const encryptedWorkers = this.localstorageService.getItem(LOCAL_STORAGE_CONSTANTS.WORKERS_KEY) as string;
    if (!encryptedWorkers) {
      return null;
    }

    const decryptedWorkers = this.cryptoService.decryptValue(encryptedWorkers) as string;
    const parsed = JSON.parse(decryptedWorkers) as CachedWorkers | WorkerDbModel[];

    // A bare array is a pre-scope cache entry: only reusable for the unscoped call.
    if (Array.isArray(parsed)) {
      return storeId === null ? parsed : null;
    }

    return parsed?.storeId === storeId ? parsed.workers : null;
  }

  setWorkersInLocalStorage(workers: WorkerDbModel[], storeId: number | null = null): void {
    const encryptedString = this.cryptoService.encryptValue(
      JSON.stringify({storeId, workers} as CachedWorkers)
    );
    this.localstorageService.setItem(LOCAL_STORAGE_CONSTANTS.WORKERS_KEY, encryptedString);
  }
}
